import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API } from '../config/env';
import { useAuthStore } from '../store/authStore';
import { useCompanyStore } from '../store/companyStore';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 실패해도 강제 로그아웃 알림/리다이렉트를 하지 않음 (초기 로그인 상태 확인용) */
    silentAuth?: boolean;
  }
}

export const apiClient = axios.create({
  baseURL: API.baseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string> | null = null;
let logoutRedirecting = false;

function logoutMessageFrom(error: unknown): string {
  const data = (error as AxiosError<{ code?: string; message?: string }>)?.response?.data;
  if (data?.code === 'SR') {
    return (
      data.message?.trim() ||
      '다른 기기에서 로그인되어 이 기기에서는 로그아웃되었습니다.'
    );
  }
  if (data?.message?.trim()) {
    return data.message.trim();
  }
  return '세션이 만료되어 로그아웃되었습니다. 다시 로그인해 주세요.';
}

async function refreshAccessTokenOnce(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post<{ accessToken: string; sessionExpiresAt?: number; rememberMe?: boolean }>('/refresh')
      .then((res) => {
        const accessToken = res.data.accessToken;
        useAuthStore.getState().setAccessToken(
          accessToken,
          res.data.sessionExpiresAt ?? null,
          res.data.rememberMe
        );
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/** 비로그인으로도 볼 수 있는 화면 — 세션 만료 시 알림/리다이렉트 없이 조용히 정리 */
function isPublicPage(): boolean {
  const path = window.location.pathname;
  return path === '/' || path === '/login';
}

function clearSessionSilently() {
  useAuthStore.getState().clearAuth();
  useCompanyStore.getState().clearCompany();
}

function forceLogout(reason?: string) {
  if (isPublicPage()) {
    clearSessionSilently();
    return;
  }
  if (logoutRedirecting) return;
  logoutRedirecting = true;
  const message =
    reason?.trim() || '세션이 만료되어 로그아웃되었습니다. 다시 로그인해 주세요.';
  try {
    window.alert(message);
  } catch {
    // ignore
  }
  useAuthStore.getState().clearAuth();
  useCompanyStore.getState().clearCompany();
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  } else {
    logoutRedirecting = false;
  }
}

/** 잘못된/권한 없는 회사 컨텍스트 → 선택값 초기화 후 회사 선택으로 */
function forceCompanyReselect() {
  useCompanyStore.getState().clearCompany();
  const path = window.location.pathname;
  if (path !== '/companies' && path !== '/login' && path !== '/setup-profile') {
    window.location.replace('/companies');
  }
}

apiClient.interceptors.request.use((config) => {
  const accessToken = useAuthStore.getState().accessToken;
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  const companyId = useCompanyStore.getState().companyId;
  if (companyId != null) {
    config.headers['X-Company-Id'] = String(companyId);
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url || '';

    const isAuthEndpoint =
      url.includes('/refresh') ||
      url.includes('/sign-in') ||
      url.includes('/sign-up') ||
      url.includes('/sign-out');

    if (
      status === 403 &&
      (code === 'ET' || code === 'NT' || code === 'SR') &&
      original &&
      !original._retry &&
      !isAuthEndpoint
    ) {
      // 이미 다른 기기 로그인으로 판정된 응답이면 refresh 재시도 없이 안내
      if (code === 'SR') {
        forceLogout(logoutMessageFrom(error));
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        const accessToken = await refreshAccessTokenOnce();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(original);
      } catch (refreshError) {
        if (isPublicPage()) {
          clearSessionSilently();
          if (original.headers) {
            delete original.headers.Authorization;
          }
          return apiClient(original);
        }
        forceLogout(logoutMessageFrom(refreshError));
        return Promise.reject(error);
      }
    }

    if (status === 403 && (code === 'ET' || code === 'NT' || code === 'SR') && isAuthEndpoint) {
      if (url.includes('/refresh') && !original?.silentAuth) {
        forceLogout(logoutMessageFrom(error));
      }
      return Promise.reject(error);
    }

    // 소속 아닌 회사 ID / 비활성 멤버 / 삭제 유예 중 멤버 접근 → 회사 선택으로 복귀
    if (
      status === 403 &&
      (code === 'CA' || code === 'MI' || code === 'GEX' || code === 'GAD' || code === 'CDP')
    ) {
      forceCompanyReselect();
      return Promise.reject(error);
    }

    if (status === 410 && code === 'CAD') {
      forceCompanyReselect();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
